import React, { useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

type ReportType = 'PROFIT_LOSS' | 'BALANCE_SHEET' | 'TRIAL_BALANCE'

const REPORT_TYPES: Array<{ value: ReportType; label: string }> = [
  { value: 'PROFIT_LOSS', label: 'Profit & Loss' },
  { value: 'BALANCE_SHEET', label: 'Balance Sheet' },
  { value: 'TRIAL_BALANCE', label: 'Trial Balance' },
]

const CURRENCIES = ['GBP', 'USD', 'EUR', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'INR', 'NZD', 'SGD', 'HKD', 'SEK', 'NOK', 'DKK']

const MultiCurrencyReports: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [reportType, setReportType] = useState<ReportType>('PROFIT_LOSS')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [baseCurrency, setBaseCurrency] = useState('GBP')
  const [includeConversion, setIncludeConversion] = useState(true)
  const [reportData, setReportData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleGenerate = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Please select both start and end dates')
      return
    }

    try {
      setIsLoading(true)
      const response = await axios.post('/api/erp/reports/multi-currency', {
        reportType,
        periodStart,
        periodEnd,
        baseCurrency,
        includeConversion,
      })
      setReportData(response.data.data)
      toast.success('Multi-currency report generated successfully')
    } catch (error: any) {
      console.error('Error generating multi-currency report:', error)
      toast.error(error.response?.data?.error || 'Failed to generate report')
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view multi-currency reports.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Multi-Currency Reports</h1>
        <p className="text-gray-600 mt-1">Generate financial reports broken down by currency</p>
      </div>

      <PageHelp articleTitle="Multi-Currency Reports" />

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Generate Report</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Type *
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {REPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Currency *
            </label>
            <select
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeConversion}
                onChange={(e) => setIncludeConversion(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Include converted amounts (convert to base currency)</span>
            </label>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleGenerate}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {isLoading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">
            {REPORT_TYPES.find(t => t.value === reportType)?.label} - Multi-Currency
          </h2>
          <div className="mb-4 text-sm text-gray-600">
            <p>Period: {new Date(reportData.period?.start || reportData.asOfDate).toLocaleDateString()} - {new Date(reportData.period?.end || reportData.asOfDate).toLocaleDateString()}</p>
            <p>Base Currency: {reportData.baseCurrency}</p>
            {reportData.includeConversion && <p>Converted amounts shown in {reportData.baseCurrency}</p>}
          </div>

          {reportType === 'PROFIT_LOSS' && reportData.income && (
            <div className="space-y-6">
              {/* Income by Currency */}
              <div>
                <h3 className="text-md font-semibold mb-3">Income by Currency</h3>
                {reportData.income.byCurrency.map((currencyData: any) => (
                  <div key={currencyData.currency} className="mb-4 border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">{currencyData.currency}</h4>
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(currencyData.total, currencyData.currency)}</div>
                        {currencyData.totalConverted && (
                          <div className="text-sm text-gray-600">
                            = {formatCurrency(currencyData.totalConverted, reportData.baseCurrency)}
                          </div>
                        )}
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Account</th>
                          <th className="text-right py-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currencyData.accounts.map((account: any) => (
                          <tr key={account.accountId} className="border-b">
                            <td className="py-1">{account.accountCode} - {account.accountName}</td>
                            <td className="text-right py-1">{formatCurrency(account.amount, currencyData.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Income:</span>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(reportData.income.grandTotal, reportData.baseCurrency)}</div>
                      {reportData.income.grandTotalConverted && (
                        <div className="text-sm text-gray-600">
                          = {formatCurrency(reportData.income.grandTotalConverted, reportData.baseCurrency)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expenses by Currency */}
              <div>
                <h3 className="text-md font-semibold mb-3">Expenses by Currency</h3>
                {reportData.expenses.byCurrency.map((currencyData: any) => (
                  <div key={currencyData.currency} className="mb-4 border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">{currencyData.currency}</h4>
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(currencyData.total, currencyData.currency)}</div>
                        {currencyData.totalConverted && (
                          <div className="text-sm text-gray-600">
                            = {formatCurrency(currencyData.totalConverted, reportData.baseCurrency)}
                          </div>
                        )}
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Account</th>
                          <th className="text-right py-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currencyData.accounts.map((account: any) => (
                          <tr key={account.accountId} className="border-b">
                            <td className="py-1">{account.accountCode} - {account.accountName}</td>
                            <td className="text-right py-1">{formatCurrency(account.amount, currencyData.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Expenses:</span>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(reportData.expenses.grandTotal, reportData.baseCurrency)}</div>
                      {reportData.expenses.grandTotalConverted && (
                        <div className="text-sm text-gray-600">
                          = {formatCurrency(reportData.expenses.grandTotalConverted, reportData.baseCurrency)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Income */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Net Income:</span>
                  <div className="text-right">
                    <div className="font-bold text-lg">{formatCurrency(reportData.netIncome.original, reportData.baseCurrency)}</div>
                    {reportData.netIncome.converted && (
                      <div className="text-sm text-gray-600">
                        = {formatCurrency(reportData.netIncome.converted, reportData.baseCurrency)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {reportType === 'BALANCE_SHEET' && (
            <div className="space-y-6">
              {/* Assets, Liabilities, Equity by Currency */}
              {['assets', 'liabilities', 'equity'].map((section) => (
                <div key={section}>
                  <h3 className="text-md font-semibold mb-3 capitalize">{section}</h3>
                  {reportData[section]?.byCurrency.map((currencyData: any) => (
                    <div key={currencyData.currency} className="mb-4 border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium">{currencyData.currency}</h4>
                        <div className="text-right">
                          <div className="font-semibold">{formatCurrency(currencyData.total, currencyData.currency)}</div>
                          {currencyData.totalConverted && (
                            <div className="text-sm text-gray-600">
                              = {formatCurrency(currencyData.totalConverted, reportData.baseCurrency)}
                            </div>
                          )}
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Account</th>
                            <th className="text-right py-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currencyData.accounts.map((account: any) => (
                            <tr key={account.accountId} className="border-b">
                              <td className="py-1">{account.accountCode} - {account.accountName}</td>
                              <td className="text-right py-1">{formatCurrency(account.amount, currencyData.currency)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {reportType === 'TRIAL_BALANCE' && (
            <div className="space-y-6">
              {reportData.byCurrency.map((currencyData: any) => (
                <div key={currencyData.currency} className="mb-4 border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">{currencyData.currency}</h4>
                    <div className="text-right">
                      <div className="text-sm">
                        <div>Debits: {formatCurrency(currencyData.totals.debits, currencyData.currency)}</div>
                        <div>Credits: {formatCurrency(currencyData.totals.credits, currencyData.currency)}</div>
                      </div>
                      {currencyData.totals.debitsConverted && (
                        <div className="text-sm text-gray-600 mt-1">
                          <div>= {formatCurrency(currencyData.totals.debitsConverted, reportData.baseCurrency)}</div>
                          <div>= {formatCurrency(currencyData.totals.creditsConverted, reportData.baseCurrency)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Account</th>
                        <th className="text-right py-2">Debit</th>
                        <th className="text-right py-2">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currencyData.accounts.map((account: any) => (
                        <tr key={account.accountId} className="border-b">
                          <td className="py-1">{account.accountCode} - {account.accountName}</td>
                          <td className="text-right py-1">{account.debit > 0 ? formatCurrency(account.debit, currencyData.currency) : '-'}</td>
                          <td className="text-right py-1">{account.credit > 0 ? formatCurrency(account.credit, currencyData.currency) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiCurrencyReports
